/**
 * Gateway send helper — lets agents send messages to their owner
 * via the plugin's local HTTP server (port 18790) or OpenClaw managed routes.
 *
 * Delivery path: sendToOwner() → plugin HTTP /send → SecureChannel.send()
 *   → Double Ratchet encrypt → WebSocket → backend → owner's app
 */

import { requestHeartbeatNow } from "./openclaw-compat.js";

export interface GatewaySendOptions {
  /** Override the gateway port (default: env GATEWAY_SEND_PORT or 18790) */
  port?: number;
  /** Override the gateway host (default: 127.0.0.1) */
  host?: string;
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
}

export interface GatewaySendResult {
  ok: boolean;
  error?: string;
}

export interface GatewayStatusResult {
  ok: boolean;
  state?: string;
  deviceId?: string;
  sessions?: number;
  error?: string;
}

function resolveBaseUrl(options?: GatewaySendOptions): string {
  const host = options?.host ?? "127.0.0.1";
  const port =
    options?.port ??
    (process.env.GATEWAY_SEND_PORT
      ? Number(process.env.GATEWAY_SEND_PORT)
      : 18790);

  // If managed routes are active, check for OpenClaw's gateway URL
  if (!options?.port && !options?.host && process.env.OPENCLAW_GATEWAY_URL) {
    return process.env.OPENCLAW_GATEWAY_URL;
  }

  return `http://${host}:${port}`;
}

/**
 * #1042 — the managed routes are registered with `auth: "gateway"`, so a call that
 * goes through OPENCLAW_GATEWAY_URL must carry the gateway's bearer token
 * (`OPENCLAW_GATEWAY_TOKEN`, the env form of `gateway.auth.token`). The loopback
 * fallback on 18790 has no auth and never receives the token.
 */
function requestHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = process.env.OPENCLAW_GATEWAY_URL ? process.env.OPENCLAW_GATEWAY_TOKEN : undefined;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function friendlyError(err: unknown): string {
  if (
    err instanceof TypeError &&
    String(err.cause ?? err.message).includes("ECONNREFUSED")
  ) {
    return "Gateway not reachable — is OpenClaw running?";
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Send a plaintext message to the agent's owner via the gateway HTTP server.
 * Never throws — returns `{ ok: false, error }` on failure.
 */
export async function sendToOwner(
  text: string,
  options?: GatewaySendOptions,
): Promise<GatewaySendResult> {
  if (typeof text !== "string" || text.trim().length === 0) {
    return { ok: false, error: "Message text must be a non-empty string" };
  }

  // Wake agent proactively before sending (fire-and-forget)
  requestHeartbeatNow({ reason: "proactive-send" }).catch(() => {});

  try {
    const base = resolveBaseUrl(options);
    // Use managed route path if gateway URL is set, else legacy /send
    const sendPath = process.env.OPENCLAW_GATEWAY_URL ? "/agentvault/send" : "/send";
    const res = await fetch(`${base}${sendPath}`, {
      method: "POST",
      headers: requestHeaders(),
      body: JSON.stringify({ text }),
      signal: options?.signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      return { ok: false, error: `HTTP ${res.status}${body ? `: ${body}` : ""}` };
    }

    return { ok: true };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}

/**
 * Send a decision request to the agent's owner via the gateway HTTP server.
 * Never throws — returns `{ ok: false, error }` on failure.
 */
export async function sendDecisionToOwner(
  request: {
    title: string;
    description?: string;
    options: Array<{ option_id: string; label: string; risk_level?: string; is_default?: boolean }>;
    context_refs?: Array<{ type: string; uri: string; label: string }>;
    deadline?: string;
    auto_action?: { option_id: string; trigger: string; description?: string };
  },
  options?: GatewaySendOptions,
): Promise<GatewaySendResult & { decision_id?: string }> {
  if (!request.title || typeof request.title !== "string") {
    return { ok: false, error: "Decision title must be a non-empty string" };
  }
  if (!Array.isArray(request.options) || request.options.length < 2) {
    return { ok: false, error: "Decision must have at least 2 options" };
  }

  requestHeartbeatNow({ reason: "proactive-decision" }).catch(() => {});

  try {
    const base = resolveBaseUrl(options);
    const path = process.env.OPENCLAW_GATEWAY_URL ? "/agentvault/decision" : "/decision";
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: requestHeaders(),
      body: JSON.stringify(request),
      signal: options?.signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      return { ok: false, error: `HTTP ${res.status}${body ? `: ${body}` : ""}` };
    }

    const data = (await res.json()) as Record<string, unknown>;
    return { ok: true, decision_id: data.decision_id as string | undefined };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}

/**
 * Send a plaintext message to a specific room via the gateway HTTP server.
 * Never throws — returns `{ ok: false, error }` on failure.
 */
export async function sendToRoom(
  roomId: string,
  text: string,
  options?: GatewaySendOptions,
): Promise<GatewaySendResult> {
  if (typeof text !== "string" || text.trim().length === 0) {
    return { ok: false, error: "Message text must be a non-empty string" };
  }
  if (!roomId) {
    return { ok: false, error: "Room ID is required" };
  }

  requestHeartbeatNow({ reason: "proactive-room-send" }).catch(() => {});

  try {
    const base = resolveBaseUrl(options);
    const sendPath = process.env.OPENCLAW_GATEWAY_URL ? "/agentvault/send" : "/send";
    const res = await fetch(`${base}${sendPath}`, {
      method: "POST",
      headers: requestHeaders(),
      body: JSON.stringify({ text, room_id: roomId }),
      signal: options?.signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      return { ok: false, error: `HTTP ${res.status}${body ? `: ${body}` : ""}` };
    }

    const data = (await res.json()) as Record<string, unknown>;
    return { ok: data.ok as boolean ?? true };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}

/**
 * Send a plaintext message to any target via the gateway HTTP server.
 * Accepts target strings: "owner", "room:<id>", "a2a:<addr>", "context".
 * Never throws — returns `{ ok: false, error }` on failure.
 */
export async function sendToTarget(
  target: string,
  text: string,
  options?: GatewaySendOptions,
): Promise<GatewaySendResult & { destination?: { kind: string; id?: string } }> {
  if (typeof text !== "string" || text.trim().length === 0) {
    return { ok: false, error: "Message text must be a non-empty string" };
  }

  requestHeartbeatNow({ reason: "proactive-target-send" }).catch(() => {});

  try {
    const base = resolveBaseUrl(options);
    const sendPath = process.env.OPENCLAW_GATEWAY_URL ? "/agentvault/send" : "/send";
    const body: Record<string, unknown> = { text };

    // Map target string to request fields
    if (target.startsWith("room:")) {
      body.room_id = target.slice(5);
    } else if (target.startsWith("a2a:")) {
      body.hub_address = target.slice(4);
    } else if (target === "context") {
      body.target = "context";
    }
    // "owner"/"default" → no extra fields (default-to-owner behavior)

    const res = await fetch(`${base}${sendPath}`, {
      method: "POST",
      headers: requestHeaders(),
      body: JSON.stringify(body),
      signal: options?.signal,
    });

    if (!res.ok) {
      const respBody = await res.text().catch(() => "");
      return { ok: false, error: `HTTP ${res.status}${respBody ? `: ${respBody}` : ""}` };
    }

    const data = (await res.json()) as Record<string, unknown>;
    return {
      ok: data.ok as boolean ?? true,
      destination: data.destination as { kind: string; id?: string } | undefined,
    };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}

/**
 * Discover available delivery targets from the gateway.
 * Never throws — returns `{ ok: false, error }` on failure.
 */
export async function listTargets(
  options?: GatewaySendOptions,
): Promise<{ ok: boolean; targets?: Array<{ kind: string; id: string; label: string; available: boolean }>; context?: { kind: string; roomId?: string }; error?: string }> {
  try {
    const base = resolveBaseUrl(options);
    const targetsPath = process.env.OPENCLAW_GATEWAY_URL ? "/agentvault/targets" : "/targets";
    const res = await fetch(`${base}${targetsPath}`, { signal: options?.signal });

    if (!res.ok) {
      return { ok: false, error: `HTTP ${res.status}` };
    }

    const data = (await res.json()) as Record<string, unknown>;
    return {
      ok: true,
      targets: data.targets as any,
      context: data.context as any,
    };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}

/**
 * Check gateway health / status.
 * Never throws — returns `{ ok: false, error }` on failure.
 */
export async function checkGateway(
  options?: GatewaySendOptions,
): Promise<GatewayStatusResult> {
  try {
    const base = resolveBaseUrl(options);
    const statusPath = process.env.OPENCLAW_GATEWAY_URL ? "/agentvault/status" : "/status";
    const res = await fetch(`${base}${statusPath}`, { signal: options?.signal });

    if (!res.ok) {
      return { ok: false, error: `HTTP ${res.status}` };
    }

    const data = (await res.json()) as Record<string, unknown>;
    return {
      ok: true,
      state: data.state as string | undefined,
      deviceId: data.deviceId as string | undefined,
      sessions: data.sessions as number | undefined,
    };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}
